import { Table } from 'react-bootstrap';
import type { SessionEventRow } from '../../apps/apis/claudeCodeDashboard/types';
import { EVENT_TYPE_HELP } from './agentTelemetryHelp';
import { HelpBadge, HelpLabel } from './HelpTooltip';

interface Props {
  events: SessionEventRow[];
}

const formatTime = (value: string): string => new Date(value).toLocaleString();

const eventBadge = (event: SessionEventRow) => {
  const description =
    event.success === false
      ? `${EVENT_TYPE_HELP[event.eventType] ?? 'Agent telemetry event.'} This event reported failure.`
      : (EVENT_TYPE_HELP[event.eventType] ?? 'Agent telemetry event.');
  if (event.success === false) {
    return (
      <HelpBadge bg="danger" description={description}>
        {event.eventType}
      </HelpBadge>
    );
  }
  if (event.eventType === 'PreToolUse' || event.eventType === 'PostToolUse') {
    return (
      <HelpBadge bg="info" description={description}>
        {event.eventType}
      </HelpBadge>
    );
  }
  if (event.eventType === 'UserPromptSubmit') {
    return (
      <HelpBadge bg="primary" description={description}>
        {event.eventType}
      </HelpBadge>
    );
  }
  if (event.eventType === 'SessionStart' || event.eventType === 'SessionEnd' || event.eventType === 'Stop') {
    return (
      <HelpBadge bg="dark" description={description}>
        {event.eventType}
      </HelpBadge>
    );
  }
  return (
    <HelpBadge bg="secondary" description={description}>
      {event.eventType}
    </HelpBadge>
  );
};

const subjectLine = (event: SessionEventRow): string => {
  if (event.skillName) {
    return `skill: ${event.skillName}`;
  }
  if (event.agentSubagentType) {
    return `agent: ${event.agentSubagentType}`;
  }
  if (event.mcpServer || event.mcpTool) {
    return `mcp: ${event.mcpServer ?? ''}/${event.mcpTool ?? ''}`;
  }
  if (event.toolName) {
    return `tool: ${event.toolName}`;
  }
  return '—';
};

const SessionEventTimeline = ({ events }: Props) => {
  if (events.length === 0) {
    return <div className="text-muted small">No events recorded for this session.</div>;
  }
  return (
    <Table size="sm" hover responsive>
      <thead>
        <tr>
          <th>
            <HelpLabel description="When the hook or telemetry event occurred.">Time</HelpLabel>
          </th>
          <th>
            <HelpLabel description="Hook or lifecycle event type reported by Claude Code or Codex.">Type</HelpLabel>
          </th>
          <th>
            <HelpLabel description="Skill, agent, MCP tool, or generic tool associated with this event.">
              Subject
            </HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description="Tool or hook duration when the runtime reports it.">Duration</HelpLabel>
          </th>
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event.eventId}>
            <td className="small text-nowrap">{formatTime(event.occurredAt)}</td>
            <td>{eventBadge(event)}</td>
            <td className="small text-monospace">{subjectLine(event)}</td>
            <td className="text-end small">{event.durationMs === null ? '—' : `${event.durationMs} ms`}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default SessionEventTimeline;
